import { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import { Loader2, ChevronLeft, FileText, Send, Printer, DollarSign, Ban, RefreshCw, Download, X, MoreHorizontal } from "lucide-react";
import { useInvoices } from '../hooks/use-invoices';
import { useToast } from '../hooks/use-toast';
import { generatePdfHtml, DEFAULT_PDF_TEMPLATE_CONFIG } from '../components/pdf/unified-pdf-template';
import { useAuth } from '../hooks/use-auth';
import { format } from "date-fns";
import { enUS } from "date-fns/locale";

// Layout Components
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';

// UI Components
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatCurrency } from '../lib/utils';
import { authInterceptor } from '../lib/auth-interceptor';
import { formatPhone } from '../lib/validation';

// Professional Components
import PaymentTracking from '../components/invoices/payment-tracking';
import PaymentForm from '../components/invoices/payment-form';
import PaymentSuccessNotification from '../components/invoices/payment-success-notification';

// Schema for payment form
const paymentSchema = z.object({
  amount: z.string().min(1, "Amount is required"),
  paymentMethod: z.string().min(1, "Payment method is required"),
  paymentDate: z.string().optional(),
  notes: z.string().optional(),
});

type PaymentFormValues = z.infer<typeof paymentSchema>;

/** Format service type for display (e.g. "roofing" -> "Roofing"). */
function formatInvoiceServiceLabel(item: { serviceType?: string; service_type?: string }): string {
  const raw = item.serviceType ?? item.service_type ?? '';
  if (!raw || raw === 'Custom') return '—';
  const lower = raw.toLowerCase();
  const titleCase = lower.charAt(0).toUpperCase() + lower.slice(1);
  const known: Record<string, string> = {
    roofing: 'Roof', roof: 'Roof', fence: 'Fence', deck: 'Deck', windows: 'Windows',
    gutters: 'Gutters', siding: 'Siding', flooring: 'Flooring', painting: 'Painting',
    electrical: 'Electrical', plumbing: 'Plumbing', hvac: 'HVAC',
  };
  return known[lower] ?? titleCase;
}

export default function InvoiceDetailPage() {
  const [, params] = useRoute("/invoices/:id");
  const invoiceId = params?.id ? parseInt(params.id) : 0;
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState("");
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState<any>(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [sendInvoiceConfirmOpen, setSendInvoiceConfirmOpen] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { 
    getInvoice, 
    registerPaymentMutation, 
    updateInvoiceStatusMutation,
    cancelInvoiceMutation,
    refundPaymentMutation,
    refetchInvoices
  } = useInvoices();
  const { data: invoice, isLoading, error, refetch } = getInvoice(invoiceId);

  // Force refresh when component mounts
  useEffect(() => {
    if (invoiceId) {
      refetch();
    }
  }, [invoiceId, refetch]);

  // Manual refresh function
  const handleRefresh = () => {
    refetch();
    toast({
      title: "Data refreshed",
      description: "Invoice data has been refreshed.",
    });
  };

  // Send invoice via email (called after confirmation)
  const handleSendInvoice = async () => {
    if (!invoice?.client?.email) {
      toast({
        title: "No Client Email",
        description: "This client does not have an email address on file.",
        variant: "destructive",
      });
      return;
    }
    setSendInvoiceConfirmOpen(false);

    try {
      setSendingInvoice(true);
      console.log('📧 Sending invoice via email:', invoiceId);
      
      const response = await fetch(`/api/protected/invoices/send-email?id=${invoiceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authInterceptor.addAuthHeaders(),
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Invoice Sent! 📧",
          description: `Invoice has been sent to ${invoice.client?.email || 'your client'}. They can now view and pay it online.`,
        });
        
        // Refresh the invoice data
        refetch();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to send invoice');
      }
    } catch (error) {
      console.error('❌ Error sending invoice:', error);
      toast({
        title: "Error Sending Invoice",
        description: error instanceof Error ? error.message : "Could not send invoice. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  // Print invoice (opens print dialog)
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({ title: "Pop-up Blocked", description: "Please allow pop-ups to print invoices.", variant: "destructive" });
      return;
    }
    const config = DEFAULT_PDF_TEMPLATE_CONFIG;
    const contractorFromInvoice = invoice?.contractor as Record<string, unknown> | undefined;
    const contractor = {
      ...contractorFromInvoice,
      companyName: user?.companyName ?? contractorFromInvoice?.companyName ?? contractorFromInvoice?.company_name ?? 'Your Company',
      email: user?.email ?? contractorFromInvoice?.email,
      phone: user?.phone ?? contractorFromInvoice?.phone ?? contractorFromInvoice?.contractor_phone,
      address: user?.address ?? contractorFromInvoice?.address ?? contractorFromInvoice?.contractor_address,
      city: user?.city ?? contractorFromInvoice?.city ?? contractorFromInvoice?.contractor_city,
      state: user?.state ?? contractorFromInvoice?.state ?? contractorFromInvoice?.contractor_state,
      zip: user?.zip ?? contractorFromInvoice?.zip ?? contractorFromInvoice?.contractor_zip,
      logoUrl: user?.logoUrl ?? contractorFromInvoice?.logoUrl ?? contractorFromInvoice?.logo_url
    };
    const htmlContent = generatePdfHtml({ data: { ...invoice, contractor }, config, type: 'invoice', isSigned: false });
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast({ title: "PDF Generated", description: "Invoice opened for printing. Use Print to PDF to save." });
  };

  // Download invoice (opens print dialog for Save as PDF)
  const handleDownload = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({ title: "Pop-up Blocked", description: "Please allow pop-ups to download invoices.", variant: "destructive" });
      return;
    }
    const config = DEFAULT_PDF_TEMPLATE_CONFIG;
    const contractorFromInvoice = invoice?.contractor as Record<string, unknown> | undefined;
    const contractor = {
      ...contractorFromInvoice,
      companyName: user?.companyName ?? contractorFromInvoice?.companyName ?? contractorFromInvoice?.company_name ?? 'Your Company',
      email: user?.email ?? contractorFromInvoice?.email,
      phone: user?.phone ?? contractorFromInvoice?.phone ?? contractorFromInvoice?.contractor_phone,
      address: user?.address ?? contractorFromInvoice?.address ?? contractorFromInvoice?.contractor_address,
      city: user?.city ?? contractorFromInvoice?.city ?? contractorFromInvoice?.contractor_city,
      state: user?.state ?? contractorFromInvoice?.state ?? contractorFromInvoice?.contractor_state,
      zip: user?.zip ?? contractorFromInvoice?.zip ?? contractorFromInvoice?.contractor_zip,
      logoUrl: user?.logoUrl ?? contractorFromInvoice?.logoUrl ?? contractorFromInvoice?.logo_url
    };
    const htmlContent = generatePdfHtml({ data: { ...invoice, contractor }, config, type: 'invoice', isSigned: false });
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
    toast({ title: "PDF Download", description: "Print dialog opened. Select 'Save as PDF' to download." });
  };

  // Payment registration form
  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: "",
      paymentMethod: "cash",
      paymentDate: new Date().toISOString().split('T')[0],
      notes: "",
    },
  });

  // Function to format dates
  const formatDate = (dateString?: string | Date | null) => {
    if (!dateString) return "Not specified";
    return format(new Date(dateString), "MMMM d, yyyy", { locale: enUS });
  };

  // Get class for badge according to invoice status
  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "!bg-green-600 !text-white !border-green-500 font-semibold";
      case "pending":
        return "!bg-orange-500 !text-white !border-orange-400 font-semibold";
      case "overdue":
        return "!bg-red-500 !text-white !border-red-400 font-semibold";
      case "partially_paid":
        return "!bg-green-600 !text-white !border-green-500 font-semibold";
      case "cancelled":
        return "!bg-slate-500 !text-white !border-slate-400 font-semibold";
      default:
        return "!bg-slate-500 !text-white !border-slate-400 font-semibold";
    }
  };

  // Readable text for status
  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "Paid";
      case "pending":
        return "Pending";
      case "overdue":
        return "Overdue";
      case "partially_paid":
        return "Partial Paid";
      case "cancelled":
        return "Cancelled";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Register a payment using the new professional form
  const handlePaymentSubmit = async (paymentData: any) => {
    try {
      const result = await registerPaymentMutation.mutateAsync({
        id: invoiceId,
        data: {
          amount: paymentData.amount.toString(),
          paymentMethod: paymentData.paymentMethod,
          paymentDate: paymentData.paymentDate,
          referenceNumber: paymentData.referenceNumber,
          notes: paymentData.notes,
        },
      });
      setIsPaymentModalOpen(false);
      // Fetch the latest invoice data after payment
      const updatedInvoiceResult = await refetch();
      const updatedInvoice = updatedInvoiceResult.data;
      // Show payment success notification with project creation details
      if (result) {
        setPaymentSuccessData({
          payment: {
            amount: parseFloat(paymentData.amount),
            method: paymentData.paymentMethod,
            paymentDate: paymentData.paymentDate || new Date().toISOString(),
          },
          invoice: {
            invoiceNumber: updatedInvoice?.invoiceNumber || `#${invoiceId}`,
            total: Number(updatedInvoice?.total ?? 0),
            amountPaid: Number(updatedInvoice?.amountPaid ?? 0),
          },
          projectUpdate: result.projectUpdate || {
            updated: false,
            message: ""
          }
        });
        setShowPaymentSuccess(true);
      }
      // Show enhanced toast notification based on project creation
      if (result.projectUpdate?.updated) {
        const projectUpdate = result.projectUpdate as { message?: string };
        toast({
          title: "Payment recorded & Project created!",
          description: `Payment recorded successfully. ${projectUpdate.message || 'Project created automatically.'}`,
        });
      } else {
        toast({
          title: "Payment recorded",
          description: "Payment has been successfully recorded.",
        });
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      toast({
        title: "Payment failed",
        description: "Failed to record payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Refund a payment
  const handleRefundPayment = async (paymentId: number, reason: string) => {
    try {
      await refundPaymentMutation.mutateAsync({
        invoiceId,
        paymentId,
        reason
      });
      
      // Refresh the invoice data
      await refetch();
      
      toast({
        title: "Payment refunded",
        description: "The payment has been refunded successfully.",
      });
    } catch (error) {
      console.error('Refund error:', error);
      toast({
        title: "Refund failed",
        description: "Failed to refund payment. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  // Mark as paid
  const handleMarkAsPaid = () => {
    updateInvoiceStatusMutation.mutate(
      { id: invoiceId, status: "paid" },
      {
        onSuccess: () => {
          toast({
            title: "Status updated",
            description: "The invoice has been marked as paid.",
          });
        },
      }
    );
  };
  
  // Cancel invoice
  const handleCancelInvoice = () => {
    cancelInvoiceMutation.mutate(
      { 
        id: invoiceId, 
        notes: cancelNotes 
      },
      {
        onSuccess: () => {
          setIsCancelModalOpen(false);
          setCancelNotes("");
          toast({
            title: "Invoice cancelled",
            description: "The invoice has been cancelled successfully.",
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content flex justify-center dashboard-2025 min-h-screen bg-[var(--dash-bg)]">
            <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-8">
              <div className="remodra-card p-8 border-green-500/30">
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <Loader2 className="h-12 w-12 animate-spin text-green-500 mb-4" />
                  <span className="text-lg text-[var(--dash-text-muted)] font-medium">Loading invoice...</span>
                  <span className="text-sm text-[var(--dash-text-muted)] mt-2">Please wait</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content flex justify-center dashboard-2025 min-h-screen bg-[var(--dash-bg)]">
            <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-8">
              <div className="remodra-card p-8 border-red-500/30">
                <div className="mb-6">
                  <Link href="/invoices">
                    <Button variant="ghost" className="mb-4 remodra-button-outline">
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Back to Invoices
                    </Button>
                  </Link>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                      <X className="h-6 w-6 text-red-500" />
                    </div>
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Error Loading Invoice</h1>
                  </div>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                    <p className="text-red-800 dark:text-red-200 font-medium mb-2">
                      {error 
                        ? `Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
                        : "Could not find the requested invoice. It may have been deleted or you don't have access to it."
                      }
                    </p>
                    {invoiceId && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        Invoice ID: {invoiceId}
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-6">
                    <h2 className="text-lg font-medium mb-2 text-slate-800 dark:text-slate-200">Suggestions:</h2>
                    <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 space-y-1 mb-4">
                      <li>Verify that you are accessing an existing invoice</li>
                      <li>To create a new invoice, you can convert an accepted estimate to an invoice</li>
                      <li>Check the estimates page to see if there are any that can be converted to invoices</li>
                    </ul>
                    <div className="flex gap-3">
                      <Link href="/invoices">
                        <Button className="remodra-button-outline">Go to Invoices List</Button>
                      </Link>
                      <Link href="/estimates">
                        <Button className="remodra-button">
                          <FileText className="h-4 w-4 mr-2" />
                          View Estimates
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate if it's overdue
  const isDueDate = invoice.dueDate ? new Date(invoice.dueDate) < new Date() : false;
  const isPending = invoice.status === "pending";
  const isOverdue = isPending && isDueDate;

  // Calculate pending amount
  const amountPaid = parseFloat(invoice.amountPaid || "0");
  const total = parseFloat(invoice.total || "0");
  const pendingAmount = total - amountPaid;
  const isPaidInFull = pendingAmount <= 0;

  // --- REMOVE the summaryRows and summary section at the top ---
  // --- NEW: Simple summary section ---
  // const summaryRows = [
  //   { label: "Total Amount", value: formatCurrency(total) },
  //   { label: "Amount Paid", value: formatCurrency(amountPaid) },
  //   { label: "Remaining Balance", value: formatCurrency(Math.max(pendingAmount, 0)) }
  // ];

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content flex justify-center dashboard-2025 min-h-screen bg-[var(--dash-bg)]">
          <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-8">
            {/* Invoice Header with Actions */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <Link href="/invoices">
                    <Button variant="ghost" className="mb-4 text-slate-700 dark:text-slate-300 hover:text-green-500 dark:hover:text-green-500 hover:bg-slate-100 dark:hover:bg-slate-700/50">
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Back to Invoices
                    </Button>
                  </Link>
                  
                  <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                    Invoice {invoice.invoice_number || invoice.invoiceNumber || `#${invoice.id}`}
                  </h1>
                  
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={getStatusClass(isOverdue ? "overdue" : invoice.status)}>
                      {getStatusText(isOverdue ? "overdue" : invoice.status)}
                    </Badge>
                    {invoice.total && (
                      <Badge variant="outline" className="text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600">
                        {formatCurrency(Number(invoice.total))}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  {/* Primary: Send Invoice */}
                  <Button
                    onClick={() => setSendInvoiceConfirmOpen(true)}
                    disabled={sendingInvoice || !invoice?.client?.email}
                    className="remodra-button"
                  >
                    {sendingInvoice ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Invoice
                      </>
                    )}
                  </Button>
                  {/* Primary: Register payment */}
                  {!isPaidInFull && (
                    <Button
                      className="remodra-button"
                      onClick={() => setIsPaymentModalOpen(true)}
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Register payment
                    </Button>
                  )}
                  {/* Secondary actions in dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="remodra-button-outline">
                        <MoreHorizontal className="h-4 w-4 mr-2" />
                        More actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[180px]">
                      <DropdownMenuItem onClick={handlePrint}>
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownload}>
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleRefresh}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </DropdownMenuItem>
                      {invoice.status !== "cancelled" && invoice.status !== "paid" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setIsCancelModalOpen(true)}
                            className="text-red-500 focus:text-red-500 focus:bg-red-50 dark:focus:bg-red-900/20"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Cancel invoice
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            
            {/* Summary - simple inline row */}
            <div className="flex flex-wrap gap-8 py-4 mb-6 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Amount</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">{formatCurrency(Number(invoice.total) || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Amount Paid</p>
                <p className="text-xl font-bold text-slate-800 dark:text-slate-200">{formatCurrency(Number(invoice.amountPaid) || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Balance Due</p>
                <p className={`text-xl font-bold ${pendingAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {formatCurrency(Math.max(pendingAmount, 0))}
                </p>
              </div>
            </div>

            {/* Client & Details - simple sections */}
            <div className="space-y-6 mb-8">
              <section>
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Client Information</h2>
                <div className="space-y-1">
                  <p className="font-medium text-slate-800 dark:text-slate-200">{invoice.client?.firstName} {invoice.client?.lastName}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{invoice.client?.email}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{formatPhone(invoice.client?.phone)}</p>
                  {invoice.client?.address && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 pt-1">
                      {invoice.client?.address}, {invoice.client?.city}, {invoice.client?.state} {invoice.client?.zip}
                    </p>
                  )}
                </div>
              </section>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <section>
                  <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Important Dates</h3>
                  <div className="space-y-1">
                    <p className="text-sm"><span className="text-slate-500">Issue date:</span> <span className="text-slate-800 dark:text-slate-200">{formatDate(invoice.issueDate)}</span></p>
                    {invoice.dueDate && (
                      <p className="text-sm"><span className="text-slate-500">Due date:</span> <span className={isOverdue ? "text-red-500 font-medium" : "text-slate-800 dark:text-slate-200"}>{formatDate(invoice.dueDate)}</span></p>
                    )}
                  </div>
                </section>
                <section>
                  <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Source Estimate</h3>
                  {invoice.estimateId || invoice.estimate_id ? (
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                        Estimate #{[invoice.estimateNumber, invoice.estimate?.estimateNumber, invoice.estimate_number]
                          .find(Boolean)?.toString().replace(/^#/, '') ?? `EST-${invoice.estimateId ?? invoice.estimate_id}`}
                        {(invoice.estimate?.title || invoice.estimateTitle) && (
                          <span className="text-slate-500 block">{invoice.estimate?.title || invoice.estimateTitle}</span>
                        )}
                      </p>
                      <Button 
                        size="sm" 
                        className="remodra-button-outline-sm shrink-0"
                        onClick={async () => {
                    const estimateId = invoice.estimateId || invoice.estimate_id;
                    if (!estimateId) return;
                    
                    try {
                      const response = await fetch(`/api/protected/estimates/get-token?id=${estimateId}`, {
                        method: 'GET',
                        headers: {
                          'Content-Type': 'application/json',
                          ...authInterceptor.addAuthHeaders(),
                        }
                      });

                      if (response.ok) {
                        const data = await response.json();
                        window.open(data.publicUrl, '_blank');
                      } else {
                        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                        toast({
                          title: "Error",
                          description: errorData.message || "Could not generate secure link. Please try again.",
                          variant: "destructive",
                        });
                      }
                    } catch (error) {
                      console.error('Error getting estimate token:', error);
                      toast({
                        title: "Error",
                        description: "Could not generate secure link. Please try again.",
                        variant: "destructive",
                      });
                    }
                    }}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Direct invoice (not from estimate)</p>
                  )}
                </section>
              </div>
            </div>
            
            <Tabs defaultValue="items" className="mb-6">
        <TabsList className="grid w-full grid-cols-3 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
          <TabsTrigger 
            value="items" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-200 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Invoice Items
          </TabsTrigger>
          <TabsTrigger 
            value="payments" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-200 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Payments
          </TabsTrigger>
          <TabsTrigger 
            value="terms" 
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=inactive]:text-slate-700 dark:data-[state=inactive]:text-slate-300 data-[state=inactive]:hover:text-green-600 dark:data-[state=inactive]:hover:text-green-400 data-[state=inactive]:hover:bg-slate-200 dark:data-[state=inactive]:hover:bg-slate-700"
          >
            Terms and Conditions
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="items">
          <div className="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden">
            <div className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/50">
                    <TableHead className="text-slate-800 dark:text-slate-300 font-semibold">Service</TableHead>
                    <TableHead className="w-[50%] text-slate-800 dark:text-slate-300 font-semibold">Description</TableHead>
                    <TableHead className="text-slate-800 dark:text-slate-300 font-semibold">Quantity</TableHead>
                    <TableHead className="text-slate-800 dark:text-slate-300 font-semibold">Unit Price</TableHead>
                    <TableHead className="text-slate-800 dark:text-slate-300 font-semibold">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.items && invoice.items.length > 0 ? (
                    invoice.items.map((item: any) => (
                      <TableRow key={item.id} className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-200">
                        <TableCell className="text-slate-800 dark:text-slate-200">
                          {formatInvoiceServiceLabel(item)}
                        </TableCell>
                        <TableCell className="font-medium text-slate-800 dark:text-slate-200">
                          {item.description}
                          {item.notes && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{item.notes}</p>}
                        </TableCell>
                        <TableCell className="text-slate-800 dark:text-slate-200">{item.quantity}</TableCell>
                        <TableCell className="text-slate-800 dark:text-slate-200">{formatCurrency(Number(item.unitPrice))}</TableCell>
                        <TableCell className="text-slate-800 dark:text-slate-200 font-medium">{formatCurrency(Number(item.amount))}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                        <div className="flex flex-col items-center">
                          <FileText className="h-8 w-8 mb-2 text-slate-600" />
                          <p>No items in this invoice</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow className="border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/30">
                    <TableCell colSpan={4} className="text-right text-slate-800 dark:text-slate-300 font-medium">Subtotal</TableCell>
                    <TableCell className="text-slate-800 dark:text-slate-200 font-medium">{formatCurrency(Number(invoice.subtotal) || 0)}</TableCell>
                  </TableRow>
                  {Number(invoice.tax) > 0 && (
                    <TableRow className="border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/30">
                      <TableCell colSpan={4} className="text-right text-slate-800 dark:text-slate-300">Tax ({invoice.tax}%)</TableCell>
                      <TableCell className="text-slate-800 dark:text-slate-200">{formatCurrency((Number(invoice.subtotal) * Number(invoice.tax)) / 100)}</TableCell>
                    </TableRow>
                  )}
                  {Number(invoice.discount) > 0 && (
                    <TableRow className="border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/30">
                      <TableCell colSpan={4} className="text-right text-slate-800 dark:text-slate-300">Discount ({invoice.discount}%)</TableCell>
                      <TableCell className="text-slate-800 dark:text-slate-200">-{formatCurrency((Number(invoice.subtotal) * Number(invoice.discount)) / 100)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow className="border-slate-300 dark:border-slate-600 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-500/10 dark:to-green-600/10">
                    <TableCell colSpan={4} className="text-right font-bold text-green-600 dark:text-green-500">Total</TableCell>
                    <TableCell className="font-bold text-green-600 dark:text-green-500 text-lg">{formatCurrency(Number(invoice.total) || 0)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="payments">
          <div className="border border-slate-200 dark:border-slate-600 rounded-lg py-6 px-4">
              <PaymentTracking
                total={Number(invoice.total) || 0}
                amountPaid={Number(invoice.amountPaid) || 0}
                payments={invoice.payments || []}
                status={invoice.status}
                dueDate={invoice.dueDate}
                invoiceNumber={invoice.invoiceNumber}
                onRefundPayment={(paymentId, reason) => handleRefundPayment(paymentId, reason)}
                isRefunding={refundPaymentMutation.isPending}
              />
          </div>
        </TabsContent>
        
        <TabsContent value="terms">
          <div className="border border-slate-200 dark:border-slate-600 rounded-lg py-6 px-4">
            {invoice.terms ? (
              <p className="whitespace-pre-wrap text-slate-800 dark:text-slate-200 leading-relaxed">{invoice.terms}</p>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 text-sm">No terms and conditions specified.</p>
            )}
            {invoice.notes && (
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-600">
                <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-200 text-sm">Additional Notes</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed">{invoice.notes}</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Payment Registration Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-2xl w-[90vw] max-h-[85vh] overflow-y-auto p-0 rounded-xl bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <DialogHeader className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <DialogTitle className="text-slate-900 dark:text-slate-100">Register Payment</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Record a payment received for this invoice
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 pb-6 bg-white dark:bg-slate-800">
            <PaymentForm
              invoice={{
                id: invoice.id,
                invoiceNumber: invoice.invoiceNumber,
                total: invoice.total,
                amountPaid: invoice.amountPaid,
                dueDate: invoice.dueDate,
                status: invoice.status
              }}
              onSubmit={handlePaymentSubmit}
              onCancel={() => setIsPaymentModalOpen(false)}
              isLoading={registerPaymentMutation.isPending}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Modal */}
      <AlertDialog open={isCancelModalOpen} onOpenChange={setIsCancelModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this invoice? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Label htmlFor="cancelNotes">Cancellation Reason (Optional)</Label>
            <Input
              id="cancelNotes"
              placeholder="Enter reason for cancellation"
              value={cancelNotes}
              onChange={(e) => setCancelNotes(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvoice}
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelInvoiceMutation.isPending}
            >
              {cancelInvoiceMutation.isPending ? "Cancelling..." : "Cancel Invoice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Invoice confirmation */}
      <AlertDialog open={sendInvoiceConfirmOpen} onOpenChange={setSendInvoiceConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send invoice</AlertDialogTitle>
            <AlertDialogDescription>
              {invoice?.client?.email ? (
                <>Send invoice {invoice?.invoice_number || invoice?.invoiceNumber || '#'} to {invoice.client?.first_name || invoice.client?.firstName} {invoice.client?.last_name || invoice.client?.lastName} ({invoice.client.email})? They will receive an email with a link to view and pay.</>
              ) : (
                <>Send invoice {invoice?.invoice_number || invoice?.invoiceNumber || '#'}? The client will receive an email with a link to view and pay.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendInvoice} disabled={sendingInvoice}>
              {sendingInvoice ? 'Sending...' : 'Send'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Success Notification */}
      {showPaymentSuccess && paymentSuccessData && (
        <PaymentSuccessNotification
          payment={paymentSuccessData.payment}
          invoice={paymentSuccessData.invoice}
          projectUpdate={paymentSuccessData.projectUpdate}
          onViewProject={() => {
            if (paymentSuccessData.projectUpdate?.updated) {
              // Navigate to projects page or specific project
              window.location.href = '/projects';
            }
          }}
          onViewInvoice={() => {
            setShowPaymentSuccess(false);
            refetch();
          }}
          onClose={() => setShowPaymentSuccess(false)}
        />
      )}
          </div>
        </div>
      </div>
    </div>
  );
}